var _excluded = ["olLayer", "customLoader", "enableMetaTiles"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useEffect, useMemo } from 'react';
import OlTileWMS from 'ol/source/TileWMS';
import OlTileGrid from 'ol/tilegrid/TileGrid';
import { useDeepCompareMemoize } from 'use-deep-compare-effect';
import { getOptions } from '../utils';
import useDefaultsCache from '../../hooks/useDefaultsCache';
var allWMSOptions = {
  attributions: undefined,
  cacheSize: undefined,
  crossOrigin: undefined,
  imageSmoothing: undefined,
  params: undefined,
  gutter: undefined,
  hidpi: undefined,
  projection: undefined,
  reprojectionErrorThreshold: undefined,
  tileClass: undefined,
  tileGrid: undefined,
  serverType: undefined,
  tileLoadFunction: undefined,
  url: undefined,
  urls: undefined,
  wrapX: undefined,
  transition: undefined
};
var DEFAULT_TILE_SIZE = [256, 256]; // formula to calculate user device dpi taken from OpenLayers repo

var userDeviceDPI = Math.floor(window.devicePixelRatio * 90 + 0.5);

var TileWMS = _ref => {
  var {
    olLayer,
    customLoader,
    enableMetaTiles
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var memoizedProps = useDeepCompareMemoize(props);
  var memoizedOptions = useMemo(() => getOptions(Object.assign(allWMSOptions, memoizedProps)), [memoizedProps]);
  var {
    cachedDefaults
  } = useDefaultsCache();
  var tileGridOrigin = useMemo(() => {
    if (!(cachedDefaults !== null && cachedDefaults !== void 0 && cachedDefaults.resolutions)) return;
    var minX = cachedDefaults.projection.extent[0];
    var minY = cachedDefaults.projection.extent[1];
    return {
      minX,
      minY
    };
  }, [cachedDefaults === null || cachedDefaults === void 0 ? void 0 : cachedDefaults.projection.extent, cachedDefaults === null || cachedDefaults === void 0 ? void 0 : cachedDefaults.resolutions]);
  var wmsLayerRequestParams = useMemo(() => {
    return enableMetaTiles ? _objectSpread({
      TILED: true,
      TILESORIGIN: "".concat(tileGridOrigin === null || tileGridOrigin === void 0 ? void 0 : tileGridOrigin.minX, ",").concat(tileGridOrigin === null || tileGridOrigin === void 0 ? void 0 : tileGridOrigin.minY),
      // When meta tiles are enabled, use user's device max dpi to display max quality map images
      FORMAT_OPTIONS: "dpi:".concat(userDeviceDPI)
    }, props.params) : props.params;
  }, [enableMetaTiles, props.params, tileGridOrigin === null || tileGridOrigin === void 0 ? void 0 : tileGridOrigin.minX, tileGridOrigin === null || tileGridOrigin === void 0 ? void 0 : tileGridOrigin.minY]);
  var tileGridConfig = useMemo(() => {
    if (!(cachedDefaults !== null && cachedDefaults !== void 0 && cachedDefaults.resolutions) || !enableMetaTiles) return;
    return new OlTileGrid({
      resolutions: cachedDefaults.resolutions,
      tileSize: DEFAULT_TILE_SIZE,
      origin: tileGridOrigin ? [tileGridOrigin.minX, tileGridOrigin.minY] : undefined
    });
  }, [cachedDefaults === null || cachedDefaults === void 0 ? void 0 : cachedDefaults.resolutions, enableMetaTiles, tileGridOrigin]);
  useEffect(() => {
    if (!olLayer) return;

    var _source = new OlTileWMS(_objectSpread(_objectSpread({}, memoizedOptions), {}, {
      params: wmsLayerRequestParams,
      tileGrid: tileGridConfig,
      // When hidpi is enabled, OpenLayers appends the dpi value to wms layer request
      // When meta tiles are enabled, pass user's device max dpi instead of dpi calculated by OpenLayers automatically
      // We are doing this to make sure map images are the best quality for the consuming user
      hidpi: enableMetaTiles ? false : true
    }));

    customLoader && _source.setTileLoadFunction(customLoader);
    olLayer.setSource(_source);
    return () => {
      var _olLayer$getSource;

      (_olLayer$getSource = olLayer.getSource()) === null || _olLayer$getSource === void 0 ? void 0 : _olLayer$getSource.dispose();
    };
  }, [customLoader, enableMetaTiles, memoizedOptions, olLayer, tileGridConfig, wmsLayerRequestParams]);
  return null;
};

export default TileWMS;